import type { RechargeRegion } from '~/helpers/recharge';

/**
 * Which Stripe publishable key the recharge card arm initialises Stripe.js
 * with, and how it survives a 3DS redirect — TV-2025.
 *
 * Plain functions so they can be unit-tested; `useLagoRecharge` supplies the
 * runtime config, the region and `sessionStorage`.
 */

export type ConfiguredStripeKeys = {
  publishableKeyEur?: string;
  publishableKeyInt?: string;
};

/**
 * The publishable key of the Stripe account that issued the `client_secret`.
 * Stripe.js refuses a secret from any other account outright, and the card
 * form never mounts.
 *
 * **The Webinterface's answer wins.** `setupIntent` returns the key alongside
 * the secret, because only the Webinterface knows which account it is on: it
 * is not a function of the environment's name. Staging's Webinterface runs on
 * the *live* accounts (`eu-staging.php` extends
 * `EU::getProductionConfigChanges()`), while the website's configured keys are
 * whatever its deploy carries — so a key read from our own config can disagree
 * with the secret, and on staging it did.
 *
 * The configured key is only the fallback, for a Webinterface that predates
 * the field. It is picked for the region the *customer* belongs to, which is
 * not necessarily the one this deployment serves: EUR and INT are separate
 * accounts (Ociris GmbH and GPORTAL Inc), and `useRechargeApi.currentRegion()`
 * routes `setupIntent` by `activeRegion` exactly as `executeApiCallActive` does
 * for gRPC.
 */
export const selectPublishableKey = (
  fromServer: string | null | undefined,
  region: RechargeRegion,
  configured: ConfiguredStripeKeys | undefined,
): string => {
  if (fromServer != null && fromServer !== '') return fromServer;

  const key =
    region === 'int'
      ? configured?.publishableKeyInt
      : configured?.publishableKeyEur;
  if (key == null || key === '') {
    throw new Error(
      `NUXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_${region.toUpperCase()} is not configured — the credit-card arm cannot mount`,
    );
  }
  return key;
};

/**
 * How the key reaches `resumeAfterRedirect`. A 3DS return lands on a fresh page
 * with only the client secret in the URL, and `retrieveSetupIntent` needs the
 * key of the account that issued it — which is the Webinterface's, not
 * necessarily our configured one. On staging the two are different accounts,
 * so falling back to the configured key there is not a degraded resume but a
 * failed one: no intent comes back, the top-up is reported as failed although
 * the card was verified, and nothing charges it.
 *
 * **The `return_url` carries it first.** It travels with the client secret, so
 * it survives everything the secret does — blocked site data, a cold browser
 * start, a return in a tab that never ran `mount`. sessionStorage is the
 * second source, for a URL that lost the parameter; the configured key is the
 * last. The key is public; putting it in a URL costs nothing.
 */
export const STRIPE_KEY_QUERY = 'stripe_key';

/**
 * Only a well-formed publishable key is taken from the URL, which the customer
 * can edit. Anything else is ignored, and the next source is tried.
 */
const PUBLISHABLE_KEY = /^pk_(live|test)_[A-Za-z0-9]+$/;

/** `returnUrl` with the key appended, keeping whatever query it already has. */
export const withStripeKey = (returnUrl: string, key: string): string => {
  const url = new URL(returnUrl);
  url.searchParams.set(STRIPE_KEY_QUERY, key);
  return url.toString();
};

export const stripeKeyFromQuery = (
  query: Record<string, unknown>,
): string | null => {
  const raw = query[STRIPE_KEY_QUERY];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === 'string' && PUBLISHABLE_KEY.test(value)
    ? value
    : null;
};

/** The second source — see `STRIPE_KEY_QUERY`. */
export const STRIPE_KEY_STORAGE = 'gp:recharge:stripe-key';

export type StripeKeyStorage = Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem'
>;

/**
 * A getter rather than the storage itself: merely touching `sessionStorage`
 * throws in some browsers when site data is blocked, and that has to land in
 * the same `catch` as a failed write.
 */
export type StripeKeyStorageSource = () => StripeKeyStorage;

export const rememberStripeKey = (
  storage: StripeKeyStorageSource,
  key: string,
): void => {
  // Removed first, separately: a write that fails must not leave an earlier
  // mount's key behind, which could belong to the other account and would make
  // the resume fail where the configured fallback might have worked.
  try {
    storage().removeItem(STRIPE_KEY_STORAGE);
  } catch {
    // Storage unavailable altogether; there is nothing stale to remove either.
  }
  try {
    storage().setItem(STRIPE_KEY_STORAGE, key);
  } catch {
    // Private mode or a full quota: the resume still has the key from the
    // `return_url`, and only a URL that lost it reaches the configured key.
  }
};

export const recallStripeKey = (
  storage: StripeKeyStorageSource,
): string | null => {
  try {
    return storage().getItem(STRIPE_KEY_STORAGE);
  } catch {
    return null;
  }
};
